import {
  Dispatch as ReactDispatch,
  ReactNode,
  createContext,
  useContext,
  useMemo,
  useReducer
} from "react";

// eslint-disable-next-line import/no-named-as-default
import produce from "immer";

import { EntityKind } from "../../../models/entityKind";

export type SortDirectionT = "asc" | "desc";
type HashMap = Record<string, string>;

// REQ-UI-TOG-001: DataTableViewType
// Using a string literal union type (not a TypeScript enum) so Redux action
// payloads serialise as plain strings in Redux DevTools and test assertions.
export type DataTableViewType = "group" | "well";

export type MonthlyFilterShape = { totalWells: number; columnCount: number };

export type Action =
  | { type: "update color map"; payload: HashMap }
  | { type: "change page"; payload: number }
  | { type: "change page size"; payload: number }
  | { type: "change sort column"; payload: string }
  | { type: "change sort direction"; payload: SortDirectionT }
  | { type: "update toolbar height"; payload: number }
  | { type: "update wells"; payload: Record<string, string[]> | null }
  | { type: "update uwis"; payload: Array<string> }
  | { type: "update overflow index"; payload: number }
  | { type: "set data table view"; payload: DataTableViewType }
  | { type: "set monthly view"; payload: boolean }
  | {
      type: "remember monthly shape";
      payload: { key: string; shape: MonthlyFilterShape };
    };

export type State = {
  colorMap: HashMap;
  page: number;
  // User-picked page size from the size changer. null = use the derived size (see useDataQuery.getPageSize).
  pageSize: Nullable<number>;
  sortColumn: string;
  sortDirection: SortDirectionT;
  toolbarHeight: number;
  wells: Record<string, string[]> | null;
  uwis: string[];
  overflowindex: number;
  entityKind?: EntityKind;
  dataTableView: DataTableViewType;
  // Monthly production by stream; independent of the group/well view.
  monthlyView: boolean;
  // Per monthly request scope, so paging is planned right on every return to Monthly.
  monthlyShapes: Record<string, MonthlyFilterShape>;
};

export type Dispatch = ReactDispatch<Action>;

const TableContext = createContext<[State, React.Dispatch<Action>] | undefined>(
  undefined
);
TableContext.displayName = "TableContext";

const initialState: State = {
  colorMap: null,
  page: 1,
  pageSize: null,
  sortColumn: "",
  sortDirection: "asc",
  toolbarHeight: 50,
  wells: null,
  uwis: [],
  overflowindex: 0,
  entityKind: EntityKind.Well,
  dataTableView: "group",
  monthlyView: false,
  monthlyShapes: {}
};

const tableReducer = (draft: State, action: Action) => {
  switch (action.type) {
    case "update color map":
      draft.colorMap = action.payload;
      break;
    case "change page":
      draft.page = action.payload;
      break;
    case "change page size":
      draft.pageSize = action.payload;
      break;
    case "change sort column":
      draft.sortColumn = action.payload;
      break;
    case "change sort direction":
      draft.sortDirection = action.payload;
      break;
    case "update toolbar height":
      draft.toolbarHeight = action.payload;
      break;
    case "update wells":
      draft.wells = action.payload;
      break;
    case "update uwis":
      draft.uwis = action.payload;
      break;
    case "update overflow index":
      draft.overflowindex = action.payload;
      break;
    case "set data table view":
      draft.dataTableView = action.payload;
      draft.page = 1;
      draft.wells = null;
      draft.uwis = [];
      break;
    case "set monthly view":
      draft.monthlyView = action.payload;
      draft.page = 1;
      // Rows per page in the Data view, wells per page in Monthly: neither carries over.
      draft.pageSize = null;
      break;
    case "remember monthly shape": {
      const { key, shape } = action.payload;
      const known = draft.monthlyShapes[key];
      if (
        known?.totalWells !== shape.totalWells ||
        known?.columnCount !== shape.columnCount
      ) {
        draft.monthlyShapes[key] = shape;
      }
      break;
    }
    default:
      throw new Error("invalid data-table action");
  }
};

const curriedReducer = produce(tableReducer);

// provider
type TableProviderT = { children: ReactNode; entityKind: EntityKind };

const TableProvider = ({ children, entityKind }: TableProviderT) => {
  const [state, dispatch] = useReducer(curriedReducer, { ...initialState, entityKind });
  const value: [State, Dispatch] = useMemo(() => [state, dispatch], [state]);

  return <TableContext.Provider value={value}>{children}</TableContext.Provider>;
};

// consumer hook
const useTable = () => {
  const context = useContext(TableContext);
  if (context === undefined) {
    throw new Error("useTable must be used within a TableProvider");
  }
  return context;
};

// context module functions
// pagination
const changePage = (dispatch: Dispatch, value: number) =>
  dispatch({ type: "change page", payload: value });
const changePageSize = (dispatch: Dispatch, value: number) =>
  dispatch({ type: "change page size", payload: value });
const changeSortColumn = (dispatch: Dispatch, value: string) =>
  dispatch({ type: "change sort column", payload: value });
const changeSortDirection = (dispatch: Dispatch, value: SortDirectionT) =>
  dispatch({ type: "change sort direction", payload: value });

// (8+8)px padding on top and bottom
const updateToolbarHeight = (dispatch: Dispatch, value: number) =>
  dispatch({ type: "update toolbar height", payload: value + 16 });

const updateWells = (dispatch: Dispatch, value: Record<string, string[]>) => {
  if (value) {
    const uwis = Object.keys(value);
    dispatch({ type: "update wells", payload: value });
    dispatch({ type: "update uwis", payload: uwis });
  }
};

const updateOverflowIndex = (dispatch: Dispatch, value: number) =>
  dispatch({ type: "update overflow index", payload: value });

const updateColorMap = (dispatch: Dispatch, value: HashMap) =>
  dispatch({ type: "update color map", payload: value });

const setDataTableView = (dispatch: Dispatch, view: DataTableViewType) =>
  dispatch({ type: "set data table view", payload: view });

const setMonthlyView = (dispatch: Dispatch, monthlyView: boolean) =>
  dispatch({ type: "set monthly view", payload: monthlyView });

const rememberMonthlyShape = (
  dispatch: Dispatch,
  key: string,
  shape: MonthlyFilterShape
) => dispatch({ type: "remember monthly shape", payload: { key, shape } });

export {
  changePage,
  rememberMonthlyShape,
  changePageSize,
  changeSortColumn,
  changeSortDirection,
  updateColorMap,
  updateToolbarHeight,
  updateWells,
  TableProvider,
  useTable,
  updateOverflowIndex,
  setDataTableView,
  setMonthlyView
};
